/**
 * AdaptyConfigDialog — Adapty 集成配置（按 App 录加密 Secret Key）
 *
 * port 自设计 SSOT design/Novel-admin/src/dialogs2.jsx 的 AdaptyConfigDialog；接真
 * /api/v1/integrations/adapty/credentials*（useAdaptyCredentials + adaptyApi）。
 * 不变量：明文 Secret Key **仅提交、永不回显**；列表只显 key_tail 掩码 + status + 同步统计。
 * 录入前后端先 ping 验证（invalid→拒、上游故障→503）；移除走 requireTyping 二次确认。
 */

import type { AdaptyCredentialResponse, AdaptyCredentialStatus } from '@novel-admin/shared';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Fragment, useState } from 'react';
import { type AppRow, useAdaptyCredentials, useApps } from '~/data/queries';
import { adaptyApi } from '~/hooks/use-adapty';
import { Icons } from '~/lib/icons';
import { AppIcon } from '~/ui/AppIcon';
import { ConfirmDialog, Dialog } from '~/ui/Dialog';
import { FormRow } from '~/ui/FormRow';
import { PasswordInput } from '~/ui/PasswordInput';
import { Select } from '~/ui/Select';
import { StatusChip } from '~/ui/StatusChip';
import { Table } from '~/ui/Table';
import { toast } from '~/ui/Toast';

const STATUS_META: Record<AdaptyCredentialStatus, { tone: 'pos' | 'warn' | 'neg'; label: string }> =
  {
    connected: { tone: 'pos', label: '已连接' },
    unverified: { tone: 'warn', label: '未验证' },
    invalid: { tone: 'neg', label: '失效' },
  };

/** Secret Key 掩码：secret_live_ + 6 圆点 + 后 5 位（与设计 SSOT adaptyMask 同口径） */
function adaptyMask(tail: string): string {
  return `secret_live_••••••${tail}`;
}

function isNativeApp(app: AppRow): boolean {
  return app.platform === 'iOS' || app.platform === 'Android';
}

function statusOf(s: string): AdaptyCredentialStatus {
  return s === 'connected' || s === 'invalid' ? s : 'unverified';
}

/** 内联「录入 / 换 Key」表单：add 选 App + 输 Key；edit 固定 App 换 Key。测试并保存 = 后端 ping 验证后加密存。 */
function AdaptyKeyForm({
  mode,
  app,
  appOptions,
  onCancel,
  onSaved,
}: {
  mode: 'add' | 'edit';
  app?: AppRow;
  appOptions: AppRow[];
  onCancel: () => void;
  onSaved: () => void;
}) {
  const [appId, setAppId] = useState(app?.id ?? appOptions[0]?.id ?? '');
  const [key, setKey] = useState('');
  const [err, setErr] = useState<string | null>(null);
  const qc = useQueryClient();

  const save = useMutation({
    mutationFn: () => adaptyApi.upsertCredential(appId, { secret_key: key.trim() }),
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: ['adapty', 'credentials'] });
      toast('已保存并验证 Adapty Key', { tone: 'pos', icon: 'IconCheck' });
      onSaved();
    },
    onError: (e) => setErr(e instanceof Error ? e.message : '保存失败'),
  });

  const submit = () => {
    setErr(null);
    if (!appId) return setErr('请选择 App');
    if (!appOptions.some((candidate) => candidate.id === appId && isNativeApp(candidate)))
      return setErr('Adapty 仅支持 iOS / Android App，请重新选择原生 App');
    if (!/^secret_live_\S{8,}$/.test(key.trim()))
      return setErr('请输入有效的 secret_live_ 开头的 Key');
    save.mutate();
  };

  return (
    <div
      style={{
        padding: 14,
        background: 'var(--bg-subtle)',
        border: '1px solid var(--border)',
        borderRadius: 8,
        marginTop: 12,
      }}
    >
      {mode === 'add' && (
        <FormRow label="选择 App" required labelWidth={120}>
          <Select
            value={appId}
            onChange={(e) => setAppId(e.target.value)}
            placeholder="选择原生 App"
          >
            {appOptions.map((a) => (
              <option key={a.id} value={a.id}>
                {a.name}
              </option>
            ))}
          </Select>
        </FormRow>
      )}
      {mode === 'edit' && app && (
        <FormRow label="App" labelWidth={120}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <AppIcon app={app} size="sm" />
            <span style={{ fontWeight: 500 }}>{app.name}</span>
            <span className="mono" style={{ fontSize: 11, color: 'var(--fg-muted)' }}>
              {app.id}
            </span>
          </div>
        </FormRow>
      )}
      <FormRow
        label="Adapty Secret API Key"
        required
        labelWidth={120}
        hint="Adapty Dashboard → 该 App → App Settings → General → API keys 获取（secret_live_…）"
      >
        <PasswordInput
          className="input mono"
          value={key}
          onChange={(e) => setKey(e.target.value)}
          placeholder="secret_live_…"
          autoFocus
        />
        <div style={{ fontSize: 11, color: 'var(--fg-muted)', marginTop: 6 }}>
          提交后加密存储，列表只显掩码、明文永不回显。
        </div>
        {err && (
          <div
            style={{
              fontSize: 11.5,
              color: 'var(--neg)',
              marginTop: 6,
              display: 'flex',
              alignItems: 'center',
              gap: 5,
            }}
          >
            <Icons.IconAlert size={12} />
            {err}
          </div>
        )}
      </FormRow>
      <div style={{ display: 'flex', gap: 8, marginTop: 10 }}>
        <button className="btn btn-accent" onClick={submit} disabled={save.isPending}>
          {save.isPending ? (
            <>
              <Icons.IconRefresh size={12} /> 验证中…
            </>
          ) : (
            <>
              <Icons.IconCheck size={12} /> 测试并保存
            </>
          )}
        </button>
        <button className="btn" onClick={onCancel} disabled={save.isPending}>
          取消
        </button>
      </div>
    </div>
  );
}

export function AdaptyConfigDialog({ onClose }: { onClose: () => void }) {
  const { data: rows } = useAdaptyCredentials();
  const { data: apps } = useApps();
  const qc = useQueryClient();

  const [adding, setAdding] = useState(false);
  const [editingAppId, setEditingAppId] = useState<string | null>(null);
  const [removing, setRemoving] = useState<AdaptyCredentialResponse | null>(null);

  const appsById = new Map(apps.map((a) => [a.id, a]));
  const nativeApps = apps.filter(isNativeApp);
  const configuredIds = new Set(rows.map((r) => r.app_id));
  const availableApps = nativeApps.filter((a) => !configuredIds.has(a.id));

  const test = useMutation({
    mutationFn: (appId: string) => adaptyApi.testCredential(appId),
    onSuccess: async (res) => {
      await qc.invalidateQueries({ queryKey: ['adapty', 'credentials'] });
      const m = STATUS_META[statusOf(res.status)];
      toast(`连接测试：${m.label}`, {
        tone: res.status === 'connected' ? 'pos' : 'warn',
        icon: res.status === 'connected' ? 'IconCheck' : 'IconAlert',
      });
    },
    onError: (e) =>
      toast(e instanceof Error ? e.message : '测试失败', { tone: 'neg', icon: 'IconAlert' }),
  });

  const remove = useMutation({
    mutationFn: (appId: string) => adaptyApi.removeCredential(appId),
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: ['adapty', 'credentials'] });
      toast('已移除 Adapty 凭证', { tone: 'warn', icon: 'IconTrash' });
    },
    onError: (e) =>
      toast(e instanceof Error ? e.message : '移除失败', { tone: 'neg', icon: 'IconAlert' }),
  });

  return (
    <Dialog
      title="Adapty 集成配置"
      subtitle="按 App 隔离配置 Secret API Key"
      icon="IconBranch"
      width={720}
      onClose={onClose}
      closeOnEsc={!removing}
      footer={
        <>
          <div style={{ flex: 1 }} />
          <button className="btn btn-accent" onClick={onClose}>
            <Icons.IconCheck size={12} /> 完成
          </button>
        </>
      }
    >
      <div style={{ padding: 18 }}>
        {/* info 条 */}
        <div
          style={{
            display: 'flex',
            gap: 8,
            padding: 12,
            background: 'var(--info-soft)',
            borderRadius: 7,
            fontSize: 12,
            color: 'var(--fg-muted)',
            lineHeight: 1.6,
            marginBottom: 16,
          }}
        >
          <Icons.IconInfo size={14} style={{ flexShrink: 0, marginTop: 2, color: 'var(--info)' }} />
          <span>
            仅 iOS / Android App 配置 Adapty；Web 使用独立商品目录。原生 App 的 Key 经 AES-GCM
            加密存储，前端**永不回显明文**，仅显示后 5 位掩码。服务端用该 Key 真同步 placement /
            产品候选。
          </span>
        </div>

        {/* section 头 */}
        <div style={{ display: 'flex', alignItems: 'center', marginBottom: 10 }}>
          <div>
            <div style={{ fontWeight: 500, fontSize: 13 }}>已配置 App</div>
            <div style={{ fontSize: 11, color: 'var(--fg-muted)', marginTop: 2 }}>
              {rows.length} 个 App 已录入 Key · 共 {nativeApps.length} 个原生 App
            </div>
          </div>
          <div style={{ flex: 1 }} />
          {availableApps.length > 0 && !adding && (
            <button className="btn btn-sm" onClick={() => setAdding(true)}>
              <Icons.IconPlus size={12} /> 添加 App 配置
            </button>
          )}
        </div>

        {/* 空态 / 表格 */}
        {rows.length === 0 && !adding ? (
          <div className="card-flat" style={{ padding: '34px 18px', textAlign: 'center' }}>
            <div
              style={{
                width: 40,
                height: 40,
                borderRadius: 10,
                background: 'var(--bg-inset)',
                color: 'var(--fg-faint)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                margin: '0 auto 12px',
              }}
            >
              <Icons.IconLock size={18} />
            </div>
            <div style={{ fontSize: 12.5, fontWeight: 500, marginBottom: 4 }}>
              还没有为任何 App 配置 Adapty Key
            </div>
            <div style={{ fontSize: 11.5, color: 'var(--fg-muted)', marginBottom: 14 }}>
              录入原生 App 的 Secret API Key 后，服务端即可同步其产品与 placement。
            </div>
            {!adding && availableApps.length > 0 && (
              <button className="btn btn-accent btn-sm" onClick={() => setAdding(true)}>
                <Icons.IconPlus size={12} /> 添加第一个 App 配置
              </button>
            )}
          </div>
        ) : (
          <Table className="tbl">
            <thead>
              <tr>
                <th>App</th>
                <th>API Key</th>
                <th>最近同步</th>
                <th style={{ width: 132 }} />
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => {
                const app = appsById.get(r.app_id);
                const m = STATUS_META[statusOf(r.status)];
                const isEditing = editingAppId === r.app_id;
                return (
                  <Fragment key={r.app_id}>
                    <tr>
                      <td>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                          {app ? <AppIcon app={app} size="sm" /> : null}
                          <div>
                            <div style={{ fontWeight: 500 }}>
                              {r.app_name ?? app?.name ?? r.app_id}
                            </div>
                            <div
                              className="mono"
                              style={{ fontSize: 10.5, color: 'var(--fg-muted)' }}
                            >
                              {r.app_id}
                            </div>
                          </div>
                        </div>
                      </td>
                      <td>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                          <span className="mono" style={{ fontSize: 11.5 }}>
                            {adaptyMask(r.key_tail)}
                          </span>
                          <StatusChip tone={m.tone} dot label={m.label} size="sm" />
                        </div>
                      </td>
                      <td>
                        {r.last_sync_at ? (
                          <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                            <span style={{ fontSize: 11.5 }}>
                              {r.last_sync_products} 产品 · {r.last_sync_placements} placement
                            </span>
                            <span
                              className="mono"
                              style={{ fontSize: 10.5, color: 'var(--fg-muted)' }}
                            >
                              {new Date(r.last_sync_at).toLocaleString()}
                            </span>
                          </div>
                        ) : (
                          <span style={{ fontSize: 11.5, color: 'var(--fg-muted)' }}>未同步</span>
                        )}
                      </td>
                      <td>
                        <div style={{ display: 'flex', gap: 4, justifyContent: 'flex-end' }}>
                          <button
                            className="btn btn-ghost btn-icon btn-sm"
                            title="测试连接"
                            onClick={() => test.mutate(r.app_id)}
                            disabled={test.isPending || !app || !isNativeApp(app)}
                          >
                            {/* test.isPending 是单个 mutation 的共享态——用 test.variables（mutationFn 入参 appId）
                                判定当前正在测试的行，仅该行 spinner 旋转（与设计 SSOT dialogs2.jsx testingId 同口径）。
                                @keyframes spin 已在 tokens.css；无 .spin 工具类，故行内引用该 keyframe 动画。 */}
                            <Icons.IconRefresh
                              size={13}
                              style={
                                test.isPending && test.variables === r.app_id
                                  ? { animation: 'spin 0.7s linear infinite' }
                                  : undefined
                              }
                            />
                          </button>
                          <button
                            className="btn btn-ghost btn-icon btn-sm"
                            title="重新输入 Key"
                            disabled={!app || !isNativeApp(app)}
                            onClick={() => setEditingAppId(isEditing ? null : r.app_id)}
                          >
                            <Icons.IconEdit size={13} />
                          </button>
                          <button
                            className="btn btn-ghost btn-icon btn-sm"
                            title="移除"
                            style={{ color: 'var(--neg)' }}
                            onClick={() => setRemoving(r)}
                          >
                            <Icons.IconTrash size={13} />
                          </button>
                        </div>
                      </td>
                    </tr>
                    {isEditing && app && isNativeApp(app) && (
                      <tr key={`${r.app_id}-edit`}>
                        <td colSpan={4} style={{ padding: 0 }}>
                          <AdaptyKeyForm
                            mode="edit"
                            app={app}
                            appOptions={nativeApps}
                            onCancel={() => setEditingAppId(null)}
                            onSaved={() => setEditingAppId(null)}
                          />
                        </td>
                      </tr>
                    )}
                  </Fragment>
                );
              })}
            </tbody>
          </Table>
        )}

        {/* 内联添加表单 */}
        {adding && (
          <AdaptyKeyForm
            mode="add"
            appOptions={availableApps}
            onCancel={() => setAdding(false)}
            onSaved={() => setAdding(false)}
          />
        )}
      </div>

      {removing && (
        <ConfirmDialog
          title={`移除 ${removing.app_name ?? removing.app_id} 的 Adapty 凭证`}
          message="移除后该 App 将无法同步 placement / 产品候选，需重新录入 Key。已配置的 product_metadata 业务效果不受影响。"
          tone="danger"
          confirmLabel="移除"
          requireTyping={removing.app_name ?? undefined}
          onConfirm={() => remove.mutate(removing.app_id)}
          onClose={() => setRemoving(null)}
        />
      )}
    </Dialog>
  );
}
